import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, Spinner, KPICard } from '../ui';
import { TrendingUp, DollarSign, Users, FileText, Receipt } from 'lucide-react';
import DirhamSymbol from '../components/DirhamSymbol';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const Portfolio = () => {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [adminKpis, setAdminKpis] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolio();
    if (user?.role === 'admin') {
      fetchAdminKpis();
      fetchChartData();
    }
  }, [user]);

  const fetchPortfolio = async () => {
    try {
      const response = await api.get('/api/portfolio');
      setPortfolio(response.data.portfolio);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminKpis = async () => {
    try {
      const response = await api.get('/api/dashboard/kpis');
      setAdminKpis(response.data.kpis);
    } catch (error) {
      console.error('Error fetching admin KPIs:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await api.get('/api/dashboard/charts');
      setChartData(response.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  // Generate chart data based on KPI values
  const profitDistributionData = adminKpis ? [
    { name: 'Admin Profit', value: adminKpis.totalAdminProfit || 0 },
    { name: 'Broker Commission', value: adminKpis.totalBrokerCommission || 0 },
  ] : [];

  // Get admin share data dynamically - show all admins if multiple, or current admin if single
  const adminShareData = adminKpis?.admins && adminKpis.admins.length > 0
    ? adminKpis.admins.map(admin => ({
      name: admin.name,
      share: admin.share || 0,
      payouts: admin.payouts || 0,
      available: admin.available || 0
    }))
    : [];

  // Use live data from backend for profit trend chart
  const profitTrendData = chartData?.monthlyProfitExpense && chartData.monthlyProfitExpense.length > 0
    ? chartData.monthlyProfitExpense.map(item => ({
      month: item.month,
      profit: item.profit || 0,
      commission: item.commission || 0,
      adminProfit: item.adminProfit || 0
    }))
    : [];

  const COLORS = ['#0A7E66', '#014F43', '#1AB98E', '#4DC8A7', '#086552'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
            My Portfolio
          </h1>
          <p className="text-primary-600 dark:text-primary-400 mt-1 text-sm sm:text-base">
            Track your investments
          </p>
        </div>
      </div>

      {/* Admin Overview KPIs - Only for admin users */}
      {user?.role === 'admin' && adminKpis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6">
          <KPICard
            title="Total Expense"
            value={
              <span className="flex items-center gap-1">
                <DirhamSymbol size={20} inline />
                {(adminKpis?.adminExpense || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            }
            icon={<DollarSign size={24} />}
            color="danger" // Red
          />
          <KPICard
            title="Total USDT Sold"
            value={
              <span className="flex items-center gap-1">
                {(adminKpis?.totalUsdtSold || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            }
            icon={<DollarSign size={24} />}
            color="info" // Blue
          />
          {/* Dynamically render admin KPIs - show all admins if multiple, or single admin with 100% if only one */}
          {adminKpis?.admins && adminKpis.admins.length > 0 ? (
            adminKpis.admins.map((admin, index) => {
              // Calculate percentage: 100% if single admin, otherwise calculate based on total
              const percentage = adminKpis.admins.length === 1
                ? 100
                : (adminKpis.totalAdminProfit > 0
                  ? Math.round((admin.share / adminKpis.totalAdminProfit) * 100)
                  : 0);

              return (
                <React.Fragment key={admin.name}>
                  <KPICard
                    title={`${admin.name} Share${adminKpis.admins.length > 1 ? ` (${percentage}%)` : ' (100%)'}`}
                    value={
                      <span className="flex items-center gap-1">
                        <DirhamSymbol size={20} inline />
                        {(admin.share || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    }
                    icon={<Users size={24} />}
                    color={index % 2 === 0 ? "info" : "secondary"}
                  />
                </React.Fragment>
              );
            })
          ) : null}
        </div>
      )}

      {/* Charts - Only for admin users */}
      {user?.role === 'admin' && adminKpis && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6">
          {/* Profit Distribution Pie Chart */}
          <Card hover={false}>
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-5">
              Profit Distribution
            </h2>
            {profitDistributionData.length > 0 && profitDistributionData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={profitDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={80}
                    fill="#014F43"
                    dataKey="value"
                  >
                    {profitDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [
                      `Đ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      ''
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value, entry) => (
                      <span style={{ color: entry.color }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] border-2 border-dashed border-primary-300 dark:border-primary-600 rounded-lg bg-primary-50/50 dark:bg-primary-900/30">
                <p className="text-primary-600 dark:text-primary-400 text-sm font-medium">No profit data available</p>
              </div>
            )}
          </Card>

          {/* Admin Share Comparison Bar Chart */}
          <Card hover={false}>
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-5">
              Admin Share Comparison
            </h2>
            {adminShareData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={adminShareData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="stroke-primary-200 dark:stroke-primary-700" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'currentColor' }}
                    className="text-primary-600 dark:text-primary-400"
                  />
                  <YAxis
                    tick={{ fill: 'currentColor' }}
                    className="text-primary-600 dark:text-primary-400"
                    tickFormatter={(value) => `Đ ${value.toLocaleString()}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [
                      `Đ ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      ''
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="share" fill="#0A7E66" name="Share" radius={[8, 8, 0, 0]} />
                  {adminShareData.length > 1 && (
                    <>
                      <Bar dataKey="payouts" fill="#DC2626" name="Payouts" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="available" fill="#014F43" name="Available" radius={[8, 8, 0, 0]} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] border-2 border-dashed border-primary-300 dark:border-primary-600 rounded-lg bg-primary-50/50 dark:bg-primary-900/30">
                <p className="text-primary-600 dark:text-primary-400 text-sm font-medium">No admin data available</p>
              </div>
            )}
          </Card>
        </div>
      )}


    </div>
  );
};

export default Portfolio;
